/**
 * https://wavesaudio.atlassian.net/browse/DSWEB-3399
 * https://developers.facebook.com/docs/facebook-login/web
 */

/** ------------------------------- APIs ---------------------------------------------- **/

import { EFacebookStatus, IFacebookResponse, IFacebookUser } from './facebook-types';

/**
 * Check Login Status of a Person
 * -------------------------------
 * The first step when your webpage loads is determining
 * if a person is already logged into your webpage with Facebook Login.
 * A call to FB.getLoginStatus starts a call to Facebook to get the login status.
 * Facebook then calls your callback function with the results.
 */
export const checkFacebookLoginStatus = () : Promise<facebook.StatusResponse> => {
    return new Promise((resolve, _reject) => {
        window.FB.getLoginStatus((response) => {
            resolve(response);
        }, true);
    });

    /*
    Response example:
    -----------------
    {
        status: 'connected',
        authResponse: {
            accessToken: '{access-token}',
            expiresIn:'{unix-timestamp}',
            reauthorize_required_in:'{seconds-until-token-expires}',
            signedRequest:'{signed-parameter}',
            userID:'{user-id}'
        }
    }
     */
};

/**
 * Log In with the Javascript SDK Login Dialog
 */
export const facebookLogin = () : Promise<facebook.StatusResponse> => {
    return new Promise((resolve, _reject) => {
        FB.login((response) => {
            resolve(response);
        }, {
            // https://developers.facebook.com/docs/permissions
            scope: 'public_profile,email'
        });
    });
};

/**
 * Log a Person Out.
 * This function call may also log the person out of Facebook.
 * Additionally, logging out of your webpage does not revoking permissions
 * the person granted your webpage during login.
 */
export const facebookLogOut = () : Promise<facebook.StatusResponse> => {
    try{
        if(window.localStorage) {
            for(let i = window.localStorage.length - 1; i>=0; i--) {
                const key = window.localStorage.key(i) || '';
                if(key.startsWith('fblst_')) {
                    window.localStorage.removeItem(key);
                }
            }
        }
    }
    catch (ex){}

    try{
        if(window.sessionStorage) {
            for(let i = window.sessionStorage.length - 1; i>=0; i--) {
                const key = window.sessionStorage.key(i) || '';
                if(key.startsWith('fbssls_')) {
                    window.sessionStorage.removeItem(key);
                }
            }
        }
    }
    catch (ex){}

    return new Promise((resolve, _reject) => {
        window?.FB?.logout((response) => {
            resolve(response);
        });
    });
};

export const getUserData = () : Promise<IFacebookUser> => {
    return new Promise((resolve, _reject) => {
        FB.api('/me', {
            fields: 'email,name'
        }, (response) => {
            resolve(response as IFacebookUser);
        });
    });
};

export const initFacebookForLogin = () => {
    window.fbAsyncInit = ()=>  {

        // https://developers.facebook.com/docs/javascript/reference/FB.init/v18.0
        FB.init({
            appId      : '546402627632636',
            cookie     : true,    // Enable cookies to allow the server to access the session.
            xfbml      : true,    // Parse social plugins on this webpage.
            version    : 'v18.0', // Use this Graph API version for this call.
            status     : false,
        });
    };

    (function(d, s, id){
        var js, fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) {return;}
        js = d.createElement(s); js.id = id;
        // @ts-ignore
        js.src = 'https://connect.facebook.net/en_US/sdk.js';
        // @ts-ignore
        fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));
};

/** ------------------------------- Flows ---------------------------------------------- **/


export const facebookSingInOrSignUpFlow = async () : Promise<IFacebookResponse|null> => {
    try{
        const statusResponse = await checkFacebookLoginStatus();

        //console.log('statusResponse', statusResponse)

        if(statusResponse.status === 'connected' && !!statusResponse.authResponse) {

            return {
                status: EFacebookStatus.LoggedIn,
                accessToken: statusResponse.authResponse.accessToken,
                userID: statusResponse.authResponse.userID,
            };
        }

        const loginResponse = await facebookLogin();

        //console.log('loginResponse', loginResponse)
        if(loginResponse.status !== 'connected' || !loginResponse.authResponse){
            return {
                status: EFacebookStatus.Error,
                accessToken: '',
                userID: '',
            };
        }

        return {
            status: EFacebookStatus.LoggedIn,
            accessToken: loginResponse.authResponse.accessToken,
            userID: loginResponse.authResponse.userID,
        };
    }
    catch (ex){
        console.error(ex);
        return null;
    }
};
